import type { TFunction } from "i18next";
import type { useUpdateSegment } from "../../api/admin";
import { useUndo } from "../../admin/undo";
import { gridStepMs } from "../geometry";
import { edgeAt, snapTime } from "../snap";
import type { Draft, TimelineRefs } from "./state";

/** How close to an edge counts as grabbing it rather than clicking the block. */
const EDGE_GRAB_PX = 6;

/** Snap radius, in pixels. Scales with zoom by construction: it is converted through msPerPx. */
const SNAP_PX = 8;

/** The two stable writers `useUpdateSegment` hands out, and nothing else from it. */
export interface EdgeMutations {
  mutateSegment: ReturnType<typeof useUpdateSegment>["mutate"];
  mutateSegmentAsync: ReturnType<typeof useUpdateSegment>["mutateAsync"];
}

/** A show edge under the pointer, if this view can edit and that block can be edited. */
export function pickEdge(r: TimelineRefs, editable: boolean, x: number, y: number) {
  if (!editable) return null;
  const scene = r.scene.current;
  if (!scene) return null;
  const b = scene.bands;
  if (y < b.contentTop || y > b.contentBottom) return null;
  for (let i = scene.shows.length - 1; i >= 0; i--) {
    const placed = scene.shows[i]!;
    const { item } = placed;
    // An exported show's audio is already cut and written to the permanent archive;
    // moving its boundaries would describe a file that no longer matches them. The
    // server refuses it too -- this only avoids offering a handle that cannot work.
    if (item.id === null || item.state === "exported") continue;
    const edge = edgeAt(x, placed.x0, placed.x1, EDGE_GRAB_PX);
    if (!edge) continue;
    // The end of a running show is drawn at the clock and owned by the segmenter: editing it
    // makes the stored span differ from the detected one, which is what stops the row being
    // extended ever again. See the matching comment in draw.drawShow.
    if (edge === "end" && item.on_air) continue;
    return { id: item.id, edge, placed };
  }
  return null;
}

/** The instant an edge would land on, snapped to whatever meaningful boundary is in reach. */
export function resolveEdge(r: TimelineRefs, rawMs: number, altKey: boolean, excludeId: number) {
  const view = r.view.current;
  // Alt disables snapping: the candidates are the meaningful instants, but a boundary
  // sometimes genuinely falls between them.
  const tolerance = altKey ? 0 : SNAP_PX * view.msPerPx;
  const segments: number[] = [];
  for (const block of r.data.current?.shows ?? []) {
    if (block.id === excludeId) continue;
    segments.push(Date.parse(block.start), Date.parse(block.end));
  }
  return snapTime(
    rawMs,
    {
      icy: r.snap.current.icy,
      scheduled: r.snap.current.scheduled,
      segments,
      gridStepMs: gridStepMs(view.msPerPx),
    },
    tolerance,
  );
}

/**
 * Save a dragged edge.
 *
 * The write is not optimistic. The database has the final say on whether two shows may
 * overlap, and a block that moves and then snaps back after a 409 is worse than one that
 * only moves once the server has agreed.
 */
export function commitEdge(m: EdgeMutations, t: TFunction, draft: Draft): void {
  const changed = draft.startMs !== draft.originalStartMs || draft.endMs !== draft.originalEndMs;
  if (!changed) return;

  const patch =
    draft.edge === "start"
      ? { start: new Date(draft.startMs).toISOString() }
      : { end: new Date(draft.endMs).toISOString() };

  m.mutateSegment(
    { id: draft.id, patch },
    {
      onSuccess: () => {
        useUndo.getState().push({
          label:
            draft.edge === "start"
              ? t("timeline.undoMoveStart", { id: draft.id })
              : t("timeline.undoMoveEnd", { id: draft.id }),
          // The inverse, not a snapshot: putting one edge back is what was undone, and
          // restoring the whole row would silently revert anything else that changed.
          run: () =>
            m.mutateSegmentAsync({
              id: draft.id,
              patch:
                draft.edge === "start"
                  ? { start: new Date(draft.originalStartMs).toISOString() }
                  : { end: new Date(draft.originalEndMs).toISOString() },
            }),
        });
      },
    },
  );
}
